/**
 * StructuredMessageRenderer.tsx
 *
 * Renders AI assistant responses with academic-style citations and content groupings.
 *
 * ## Why This Component Exists
 *
 * The Ask Arwen chat assistant analyzes social media content and returns structured
 * responses that reference specific pieces of content. This component transforms those
 * responses into a readable format with clickable citations, similar to academic papers.
 *
 * ## How It Works
 *
 * 1. **Markdown Rendering**: The response text is rendered as markdown, allowing the
 *    assistant to format responses with headings, lists, bold text, etc.
 *
 * 2. **Citation Injection**: As markdown is rendered, we intercept text nodes and scan
 *    for content ID patterns (e.g., "[#123]", "#123", "content id: 123"). These are
 *    replaced with clickable inline citations like "[1]" that show tooltips on hover.
 *
 * 3. **Citation Index Mapping**: Citations use 1-based indices (like academic papers)
 *    rather than raw content IDs. A Map tracks contentId -> displayIndex so the same
 *    content always gets the same citation number throughout the response.
 *
 * 4. **Expandable Panels**: Below the main text, collapsible panels show full citation
 *    details and content groupings (categories), letting users dive deeper if needed.
 *
 * ## Architecture Decisions
 *
 * - **Context Pattern**: Citation data is passed through a CitationContext object rather
 *   than prop drilling, since the processChildren function recurses through arbitrary
 *   React children and needs access to citation mappings at every level.
 *
 * - **Regex-based Parsing**: Content IDs are detected via regex because the LLM may
 *   format them inconsistently (brackets, hashes, "content id:" prefix, etc.). The
 *   flexible pattern handles all observed variations.
 *
 * - **Tooltip Positioning**: Tooltips dynamically position above or below based on
 *   available viewport space, preventing cutoff at screen edges.
 *
 * - **Separate Expand States**: Citations and categories panels have independent
 *   expand/collapse states so users can view one without the other.
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { ChevronDownIcon, ChevronRightIcon, EyeIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { Citation, ContentCategory, ParsedAssistantMessage } from './types'

type StructuredMessageRendererProps = {
    message: ParsedAssistantMessage
    /** Callback when user clicks a citation to view the original content */
    onContentReferenceClick?: (contentId: number) => void
}

/**
 * Regex to match content ID patterns in LLM-generated text.
 *
 * The LLM may reference content in various formats:
 * - "[#123]" - bracketed with hash (most common)
 * - "#123" - hash prefix only
 * - "[123]" - bracketed without hash
 * - "content id: 123" - verbose form
 * - "id: 123" - abbreviated verbose form
 *
 * The pattern captures the numeric ID in group 1, allowing us to extract it
 * regardless of which format the LLM chose to use.
 */
const CONTENT_ID_PATTERN = /(?:content\s+id\s*:?\s*|id\s*:?\s*|\[#?|#)(\d+)(?:\])?/gi

/**
 * Context object passed through the component tree during markdown rendering.
 *
 * This pattern is used instead of prop drilling because processChildren() recurses
 * through arbitrary React children (strings, arrays, elements) and needs citation
 * data at every level without modifying the ReactMarkdown component's props.
 */
type CitationContext = {
    /** Maps raw content IDs to 1-based display indices (e.g., 12345 -> 1) */
    contentIdToIndex: Map<number, number>
    /** Maps 1-based display indices to actual content IDs (e.g., 1 -> 12345) */
    indexToContentId: Map<number, number>
    /** Full citation objects for tooltip display */
    citations: Citation[]
    /** Click handler to navigate to source content */
    onContentClick?: (contentId: number) => void
}

/**
 * InlineCitation - A clickable citation marker rendered inline with text.
 *
 * Displays as a subtle "[1]" style reference that:
 * - Clicks to navigate to the source content
 * - Shows a tooltip on hover with the quoted text
 * - Dynamically positions tooltip above/below based on viewport space
 *
 * The design mimics academic paper citations - unobtrusive during reading
 * but providing quick access to sources when needed.
 */
type InlineCitationProps = {
    /** 1-based display index shown to user (e.g., 1, 2, 3) */
    index: number
    /** Actual content ID for click handler */
    contentId: number
    /** Full citation data for tooltip (quote text) */
    citation?: Citation
    onContentClick?: (contentId: number) => void
}

function InlineCitation({ index, contentId, citation, onContentClick }: InlineCitationProps) {
    const [showTooltip, setShowTooltip] = useState(false)
    const [tooltipPosition, setTooltipPosition] = useState<'above' | 'below'>('above')
    const buttonRef = useRef<HTMLButtonElement>(null)

    // Dynamically position tooltip based on available viewport space.
    // Tooltips are ~200px tall, so we prefer "above" if there's enough room,
    // otherwise flip to "below" to prevent cutoff at screen edges.
    useEffect(() => {
        if (showTooltip && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            const spaceAbove = rect.top
            const spaceBelow = window.innerHeight - rect.bottom
            setTooltipPosition(spaceAbove > 200 || spaceAbove > spaceBelow ? 'above' : 'below')
        }
    }, [showTooltip])

    return (
        <span className="relative inline">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => onContentClick?.(contentId)}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className="text-gray-400 hover:text-primary-600 hover:underline cursor-pointer transition-colors"
            >
                [{index}]
            </button>
            {showTooltip && citation && (
                <div
                    className={`absolute z-50 w-64 p-2.5 bg-gray-900 text-white rounded-lg shadow-lg text-xs ${
                        tooltipPosition === 'above'
                            ? 'bottom-full mb-1'
                            : 'top-full mt-1'
                    } left-1/2 -translate-x-1/2`}
                >
                    <div className="line-clamp-3 leading-relaxed">
                        &ldquo;{citation.quote}&rdquo;
                    </div>
                    <div className="mt-1.5 text-gray-400 text-[10px]">
                        Click to view source
                    </div>
                </div>
            )}
        </span>
    )
}

/**
 * Scans text for content ID references and replaces them with InlineCitation components.
 *
 * This function is the core of the citation system. It:
 * 1. Finds all number patterns in the text (e.g., "[#123]", "#456", "[#1]")
 * 2. Interprets each number in two ways:
 *    a. As an actual content ID (if it exists in contentIdToIndex map)
 *    b. As a citation display index (if it exists in indexToContentId map)
 * 3. Replaces matched patterns with InlineCitation components
 * 4. Preserves non-matching text as plain strings
 *
 * The dual-interpretation handles cases where the LLM may write display indices
 * in the text (e.g., "[#1]" for "first citation") while putting actual content IDs
 * in the citations array. This ensures clicks navigate to the correct content.
 *
 * Returns an array of strings and JSX elements that can be rendered as React children.
 *
 * @example
 * // If LLM uses actual content IDs in text:
 * Input: "See comment [#12345] for details"
 * Output: ["See comment ", <InlineCitation index={1} contentId={12345} />, " for details"]
 *
 * @example
 * // If LLM uses display indices in text but actual IDs in citations:
 * Input: "See comment [#1] for details" (with citations[0].content_id = 12345)
 * Output: ["See comment ", <InlineCitation index={1} contentId={12345} />, " for details"]
 */
function parseContentReferences(
    text: string,
    ctx: CitationContext
): (string | JSX.Element)[] {
    // Skip parsing if no click handler - citations would be non-interactive
    if (!ctx.onContentClick) {
        return [text]
    }

    const result: (string | JSX.Element)[] = []
    let match: RegExpExecArray | null
    let keyIndex = 0
    let lastIndex = 0

    // Reset regex state (global regexes maintain state between calls)
    CONTENT_ID_PATTERN.lastIndex = 0

    while ((match = CONTENT_ID_PATTERN.exec(text)) !== null) {
        const extractedNumber = parseInt(match[1], 10)
        const matchStart = match.index
        const matchEnd = match.index + match[0].length

        // Add text before this match
        if (matchStart > lastIndex) {
            result.push(text.slice(lastIndex, matchStart))
        }

        // Try to interpret the extracted number in two ways:
        // 1. As an actual content ID (original design)
        // 2. As a citation display index (fallback for when LLM uses indices)
        let contentId: number | undefined
        let citationIndex: number | undefined
        let citation: Citation | undefined

        // First, try treating it as a content ID
        const indexFromContentId = ctx.contentIdToIndex.get(extractedNumber)
        if (indexFromContentId !== undefined) {
            // The extracted number is a valid content ID
            contentId = extractedNumber
            citationIndex = indexFromContentId
            citation = ctx.citations.find((c) => c.content_id === contentId)
        } else {
            // Try treating it as a citation index (1-based)
            const contentIdFromIndex = ctx.indexToContentId.get(extractedNumber)
            if (contentIdFromIndex !== undefined) {
                // The extracted number is a citation index, map to actual content ID
                contentId = contentIdFromIndex
                citationIndex = extractedNumber
                citation = ctx.citations.find((c) => c.content_id === contentId)
            }
        }

        if (contentId !== undefined && citationIndex !== undefined) {
            // Known citation - render as interactive InlineCitation
            result.push(
                <InlineCitation
                    key={`ref-${keyIndex++}`}
                    index={citationIndex}
                    contentId={contentId}
                    citation={citation}
                    onContentClick={ctx.onContentClick}
                />
            )
        } else {
            // LLM mentioned a number that wasn't in the citations array.
            // This can happen if the LLM hallucinates an ID or references content
            // that was filtered out. We render it as muted text to avoid broken
            // links while still showing what the LLM wrote.
            result.push(
                <span key={`ref-${keyIndex++}`} className="text-gray-400">
                    {match[0]}
                </span>
            )
        }

        lastIndex = matchEnd
    }

    // Add remaining text
    if (lastIndex < text.length) {
        result.push(text.slice(lastIndex))
    }

    return result.length > 0 ? result : [text]
}

/**
 * Recursively processes React children to inject citation components.
 *
 * ReactMarkdown renders text content as children, which may be:
 * - A single string: "Hello [#123] world"
 * - An array of mixed content: ["Hello ", <strong>important</strong>, " [#123]"]
 * - Other React elements (passed through unchanged)
 *
 * This function handles each case, applying parseContentReferences only to
 * string children while preserving the structure of other React elements.
 *
 * The optimization of returning plain strings when no citations are found
 * avoids unnecessary wrapper elements that could affect styling.
 */
function processChildren(
    children: React.ReactNode,
    ctx: CitationContext
): React.ReactNode {
    if (!ctx.onContentClick) {
        return children
    }

    if (typeof children === 'string') {
        const parsed = parseContentReferences(children, ctx)
        // Avoid wrapping in fragment if nothing changed (preserves text node)
        return parsed.length === 1 && typeof parsed[0] === 'string' ? parsed[0] : <>{parsed}</>
    }

    if (Array.isArray(children)) {
        return children.map((child, index) => {
            if (typeof child === 'string') {
                const parsed = parseContentReferences(child, ctx)
                return parsed.length === 1 && typeof parsed[0] === 'string' ? (
                    parsed[0]
                ) : (
                    <span key={index}>{parsed}</span>
                )
            }
            // Non-string children (React elements) pass through unchanged
            return child
        })
    }

    return children
}

type CitationsPanelProps = {
    citations: Citation[]
    contentIdToIndex: Map<number, number>
    expanded: boolean
    onToggle: () => void
    onContentClick?: (contentId: number) => void
}

/**
 * CitationCard - Expandable card showing a single citation's details.
 *
 * Each card displays:
 * - The citation index [1], [2], etc.
 * - A "View source" link to navigate to the content
 * - The quoted text (truncated to 120 chars by default)
 * - Optionally, a "Why cited" explanation when expanded
 *
 * Cards have their own expand/collapse state independent of the parent panel,
 * allowing users to expand specific citations of interest.
 */
type CitationCardProps = {
    citation: Citation
    /** 1-based display index */
    index: number
    onContentClick?: (contentId: number) => void
}

function CitationCard({ citation, index, onContentClick }: CitationCardProps) {
    const [expanded, setExpanded] = useState(false)
    const truncatedQuote = citation.quote.length > 120
        ? citation.quote.slice(0, 120) + '...'
        : citation.quote

    return (
        <div className="rounded-lg bg-gray-50 text-sm overflow-hidden">
            <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs font-medium flex-shrink-0">
                                [{index}]
                            </span>
                            <button
                                type="button"
                                onClick={() => onContentClick?.(citation.content_id)}
                                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                            >
                                <EyeIcon className="h-3 w-3" />
                                View source
                            </button>
                        </div>
                        <p className="mt-1.5 text-gray-600 italic leading-relaxed">
                            &ldquo;{expanded ? citation.quote : truncatedQuote}&rdquo;
                        </p>
                    </div>
                    {(citation.relevance || citation.quote.length > 120) && (
                        <button
                            type="button"
                            onClick={() => setExpanded(!expanded)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title={expanded ? 'Show less' : 'Show more'}
                        >
                            {expanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {expanded && citation.relevance && (
                <div className="px-3 pb-3 pt-0">
                    <div className="pl-6 border-l-2 border-gray-200">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            Why cited
                        </div>
                        <p className="text-gray-600">{citation.relevance}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

/**
 * CitationsPanel - Collapsible panel showing all citations for a response.
 *
 * Appears below the main response text with a "N sources" header. When expanded,
 * shows individual CitationCard components sorted by their display index.
 *
 * The sorting ensures citations appear in the same order they're referenced
 * in the text (by display index), not by their raw content IDs.
 */
function CitationsPanel({ citations, contentIdToIndex, expanded, onToggle, onContentClick }: CitationsPanelProps) {
    // Sort citations by display index so [1] appears before [2], etc.
    // This provides a consistent reading order matching the text references.
    const sortedCitations = useMemo(() => {
        return [...citations].sort((a, b) => {
            const idxA = contentIdToIndex.get(a.content_id) ?? 999
            const idxB = contentIdToIndex.get(b.content_id) ?? 999
            return idxA - idxB
        })
    }, [citations, contentIdToIndex])

    return (
        <div className="border-t border-gray-200 pt-3 mt-4">
            <button
                onClick={onToggle}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors w-full"
            >
                {expanded ? (
                    <ChevronDownIcon className="h-4 w-4" />
                ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                )}
                <DocumentTextIcon className="h-4 w-4" />
                <span>{citations.length} source{citations.length !== 1 ? 's' : ''}</span>
            </button>

            {expanded && (
                <div className="mt-3 space-y-2">
                    {sortedCitations.map((citation) => {
                        const citationIndex = contentIdToIndex.get(citation.content_id) ?? 0
                        return (
                            <CitationCard
                                key={citation.content_id}
                                citation={citation}
                                index={citationIndex}
                                onContentClick={onContentClick}
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/**
 * CategoriesPanel - Collapsible panel showing thematic content groupings.
 *
 * The AI assistant may group related content into categories (e.g., "Positive feedback",
 * "Feature requests", "Bug reports"). This panel displays those groupings with:
 * - Category name and summary
 * - List of content IDs (shown as citation indices) belonging to each category
 *
 * This helps users understand themes across the analyzed content without reading
 * every individual piece.
 */
type CategoriesPanelProps = {
    categories: ContentCategory[]
    contentIdToIndex: Map<number, number>
    expanded: boolean
    onToggle: () => void
    onContentClick?: (contentId: number) => void
}

function CategoriesPanel({ categories, contentIdToIndex, expanded, onToggle, onContentClick }: CategoriesPanelProps) {
    return (
        <div className="border-t border-gray-200 pt-3 mt-4">
            <button
                onClick={onToggle}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
                {expanded ? (
                    <ChevronDownIcon className="h-4 w-4" />
                ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                )}
                {categories.length} content group{categories.length !== 1 ? 's' : ''}
            </button>

            {expanded && (
                <div className="mt-3 space-y-3">
                    {categories.map((category, idx) => (
                        <div
                            key={idx}
                            className="rounded-lg bg-gray-50 p-3 text-sm"
                        >
                            <div className="font-medium text-gray-900">{category.name}</div>
                            <p className="mt-1 text-gray-600">{category.summary}</p>
                            <div className="mt-2 text-xs text-gray-400">
                                {category.content_ids.map((contentId, cidx) => {
                                    const citationIndex = contentIdToIndex.get(contentId)
                                    return (
                                        <button
                                            key={contentId}
                                            type="button"
                                            onClick={() => onContentClick?.(contentId)}
                                            className="hover:text-primary-600 transition-colors"
                                        >
                                            [{citationIndex ?? '?'}]{cidx < category.content_ids.length - 1 ? ' ' : ''}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

/**
 * Main component for rendering structured AI assistant responses.
 *
 * Orchestrates the rendering pipeline:
 * 1. Builds citation index mapping from the message's citations array
 * 2. Renders markdown with citation injection via custom ReactMarkdown components
 * 3. Renders expandable CitationsPanel if citations exist
 * 4. Renders expandable CategoriesPanel if categories exist
 */
export default function StructuredMessageRenderer({
    message,
    onContentReferenceClick
}: StructuredMessageRendererProps) {
    const [citationsExpanded, setCitationsExpanded] = useState(false)
    const [categoriesExpanded, setCategoriesExpanded] = useState(false)

    // Build bidirectional mappings between content IDs and display indices.
    // Example: If citations reference content IDs [54321, 12345, 99999],
    // they become display indices [1, 2, 3] respectively.
    // contentIdToIndex: 54321 -> 1, 12345 -> 2, 99999 -> 3
    // indexToContentId: 1 -> 54321, 2 -> 12345, 3 -> 99999
    //
    // The indexToContentId map allows us to handle cases where the LLM writes
    // display indices in the text (e.g., [#1]) instead of actual content IDs.
    const { contentIdToIndex, indexToContentId } = useMemo(() => {
        const idToIdx = new Map<number, number>()
        const idxToId = new Map<number, number>()
        message.citations.forEach((citation, idx) => {
            const displayIndex = idx + 1 // 1-based indexing
            idToIdx.set(citation.content_id, displayIndex)
            idxToId.set(displayIndex, citation.content_id)
        })
        return { contentIdToIndex: idToIdx, indexToContentId: idxToId }
    }, [message.citations])

    // Context object passed to all child renderers for citation lookup
    const citationContext: CitationContext = {
        contentIdToIndex,
        indexToContentId,
        citations: message.citations,
        onContentClick: onContentReferenceClick
    }

    return (
        <div>
            {/* Main Response - Markdown with injected citations */}
            <div className="arwen-markdown">
                <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    className="leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                    components={{
                        // Override text-containing elements to inject citations.
                        // We target p, li, and td as these are where inline text appears.
                        // Other elements (headings, code blocks) don't typically contain citations.
                        p: ({ children, ...props }) => (
                            <p {...props}>
                                {processChildren(children, citationContext)}
                            </p>
                        ),
                        li: ({ children, ...props }) => (
                            <li {...props}>
                                {processChildren(children, citationContext)}
                            </li>
                        ),
                        td: ({ children, ...props }) => (
                            <td {...props}>
                                {processChildren(children, citationContext)}
                            </td>
                        )
                    }}
                >
                    {message.text}
                </ReactMarkdown>
            </div>

            {/* Citations Panel */}
            {message.citations.length > 0 && (
                <CitationsPanel
                    citations={message.citations}
                    contentIdToIndex={contentIdToIndex}
                    expanded={citationsExpanded}
                    onToggle={() => setCitationsExpanded(!citationsExpanded)}
                    onContentClick={onContentReferenceClick}
                />
            )}

            {/* Categories Panel */}
            {message.categories.length > 0 && (
                <CategoriesPanel
                    categories={message.categories}
                    contentIdToIndex={contentIdToIndex}
                    expanded={categoriesExpanded}
                    onToggle={() => setCategoriesExpanded(!categoriesExpanded)}
                    onContentClick={onContentReferenceClick}
                />
            )}
        </div>
    )
}
